/**
 * Jolly Phonics — letter-sound helpers.
 *
 * Jolly Phonics teaches the 42 letter sounds children need to blend words,
 * not the alphabet names. When we "sound out" a word for a pre-reader we
 * speak /a/-/t/ ("a-t"), not "ay-tee". This lib provides:
 *
 *   • JOLLY_SOUNDS      — letter/digraph -> spoken pronunciation hint
 *   • segmentWord(w)    — split a word into Jolly Phonics phonemes
 *                         (greedy digraph-first tokeniser)
 *   • soundOutWord(w, opt) — speak each phoneme in sequence using
 *                         SpeechSynthesis (async, respects `rate`).
 *
 * Design notes:
 *   • We don't try to be a full IPA/phoneme engine. English spelling is
 *     wildly irregular; the goal is a good-enough sound-out for a
 *     Seedling/Explorer to hear the sounds of each letter/digraph. Kids
 *     who need precise phoneme mapping should hear a human read the
 *     word (that's what our "Hear the word" button already does).
 *   • Digraphs are matched first (sh, ch, th, ng, ai, ee, ie, oa, ou,
 *     oi, er, ar, oo, ue) — the full Jolly Phonics group-6/7 set.
 *   • Each spoken phoneme uses an SSML-ish tiny word wrapper so the
 *     browser TTS pronounces /a/ as short-a rather than the letter
 *     name. E.g., "ah" for /a/, "buh" for /b/, "sh" for /sh/. This is
 *     the closest we can get with plain SpeechSynthesisUtterance.
 */

// Short pronounceable hints — spoken by TTS to imitate the Jolly sound.
// These are heard, not seen. Each is 2-3 chars so the browser voice
// says the phoneme clearly.
export const JOLLY_SOUNDS = {
  // Group 1
  s: "sss", a: "aah", t: "tuh", i: "ih",  p: "puh", n: "nuh",
  // Group 2
  c: "kuh", k: "kuh", e: "eh",  h: "huh", r: "ruh", m: "mmm", d: "duh",
  // Group 3
  g: "guh", o: "oh",  u: "uh",  l: "lll", f: "fff", b: "buh",
  // Group 4
  j: "juh", z: "zzz", w: "wuh", v: "vuh", y: "yuh", x: "ks",
  // Group 5
  q: "kwuh",
  // Group 6 & 7 — digraphs
  sh: "shhh", ch: "chuh", th: "thhh", ng: "ng",
  ai: "ay",   ee: "eee",  ie: "eye",  oa: "oh",
  ou: "ow",   oi: "oy",   er: "urr",  ar: "arr",
  oo: "oo",   ue: "yoo",
};

// Ordered by longest-first so `sh` beats `s`+`h`.
const DIGRAPHS = ["ng", "sh", "ch", "th", "ai", "ee", "ie", "oa", "ou", "oi", "er", "ar", "oo", "ue"];

/**
 * Break a word into a sequence of Jolly Phonics phonemes (greedy).
 * Unknown characters (numbers, apostrophes) are dropped. Case-insensitive.
 */
export function segmentWord(word) {
  const lc = String(word || "").toLowerCase().replace(/[^a-z]/g, "");
  const out = [];
  let i = 0;
  while (i < lc.length) {
    const two = lc.slice(i, i + 2);
    if (DIGRAPHS.includes(two)) {
      out.push(two);
      i += 2;
    } else {
      out.push(lc[i]);
      i += 1;
    }
  }
  return out;
}

/**
 * Get the spoken phoneme hint for a segment. Returns the segment itself
 * if we have no explicit mapping (fine for TTS fallback).
 */
export function phonemeSpeechText(segment) {
  return JOLLY_SOUNDS[segment] || segment;
}

/**
 * Speak a word phoneme-by-phoneme, pausing briefly between segments.
 * Optionally blends the word at the end (repeats the full word at
 * natural pace) — recommended for pre-readers.
 *
 * When `useHumanTTS=true` and a `speakFn` (usually the /api/tts
 * playHuman helper) is provided, each phoneme is played via the
 * OpenAI-backed pipeline in sequence — that gives a real human voice.
 * Otherwise falls back to browser SpeechSynthesis for offline mode.
 *
 * Returns a Promise that resolves when all speech is done.
 */
export function soundOutWord(word, opts = {}) {
  const {
    rate = 0.35,
    blendAtEnd = true,
    lang = "en-US",
    useHumanTTS = false,
    speakFn = null,
    interSegmentDelayMs = 260,
  } = opts;

  return new Promise(async (resolve) => {
    try {
      const segs = segmentWord(word);
      if (segs.length === 0) { resolve(); return; }

      // ── Human-voice path via OpenAI TTS ─────────────────────────
      // Simplified: send ONE TTS call with all phonemes joined by "..."
      // pauses + the full word at the end. OpenAI's nova voice reads
      // pauses naturally, so we get slow, clear articulation without
      // multiple sequential network calls or client-side timing hacks.
      if (useHumanTTS && typeof speakFn === "function") {
        const spoken = segs.map(phonemeSpeechText).join("... ");
        const script = blendAtEnd
          ? `${spoken}.  ...  ${word}.`
          : `${spoken}.`;
        try { await speakFn(script); } catch { /* ignore */ }
        resolve();
        return;
      }

      // ── Browser SpeechSynthesis fallback ────────────────────────
      if (typeof window === "undefined" || !("speechSynthesis" in window)) {
        resolve();
        return;
      }
      window.speechSynthesis.cancel();
      const voice = pickHumanVoice(lang);
      const utts = segs.map((s) => {
        const u = new SpeechSynthesisUtterance(phonemeSpeechText(s) + ".  ");
        u.rate = rate;
        u.pitch = 1.05;
        u.lang = lang;
        if (voice) u.voice = voice;
        return u;
      });
      if (blendAtEnd) {
        const full = new SpeechSynthesisUtterance(word);
        full.rate = 0.75;
        full.pitch = 1.0;
        full.lang = lang;
        if (voice) full.voice = voice;
        utts.push(full);
      }
      let done = 0;
      const tryFinish = () => {
        done += 1;
        if (done >= utts.length) resolve();
      };
      utts.forEach((u) => {
        u.onend = tryFinish;
        u.onerror = tryFinish;
        window.speechSynthesis.speak(u);
      });
    } catch {
      resolve();
    }
  });
}

/** Say a single letter's Jolly Phonics sound (used by Seedling surfaces). */
export function speakLetterSound(letter, opts = {}) {
  return soundOutWord(String(letter || "")[0] || "", { ...opts, blendAtEnd: false });
}

/**
 * Pick the best-sounding available TTS voice for a given language. The
 * browser's default `speechSynthesis` voice on most devices is a
 * robotic system voice; we search the installed voice list for
 * higher-quality named human voices (Google/Apple Neural/etc) first.
 *
 * The list is voice quality descending — first match wins.
 */
const PREFERRED_VOICES = [
  // Apple neural / premium
  "Samantha (Enhanced)", "Ava (Premium)", "Ava", "Karen (Enhanced)", "Karen",
  "Daniel (Enhanced)", "Daniel", "Kate (Enhanced)", "Kate",
  // Google Wavenet / Neural (Chrome / Android)
  "Google UK English Female", "Google UK English Male",
  "Google US English", "Google US English Female", "Google US English Male",
  // Microsoft Neural
  "Microsoft Aria Online (Natural)", "Microsoft Jenny Online (Natural)",
  "Microsoft Guy Online (Natural)", "Microsoft Sonia Online (Natural)",
  // Common Apple fallbacks
  "Samantha", "Alex", "Fiona", "Moira",
];

export function pickHumanVoice(lang = "en-US") {
  try {
    if (typeof window === "undefined" || !window.speechSynthesis) return null;
    const voices = window.speechSynthesis.getVoices() || [];
    if (voices.length === 0) return null;
    const langPrefix = String(lang).slice(0, 2).toLowerCase();
    const langMatches = voices.filter((v) => (v.lang || "").toLowerCase().startsWith(langPrefix));
    // 1) Preferred named voices first (exact match on our list).
    for (const wanted of PREFERRED_VOICES) {
      const found = langMatches.find((v) => v.name === wanted) ||
                    voices.find((v) => v.name === wanted);
      if (found) return found;
    }
    // 2) Any voice flagged as `localService = false` (usually cloud/high-
    //    quality) in the child's language.
    const cloud = langMatches.find((v) => v.localService === false);
    if (cloud) return cloud;
    // 3) Any voice whose name hints at "natural" / "neural" / "enhanced".
    const hinted = langMatches.find((v) => /neural|natural|enhanced|premium|wavenet/i.test(v.name || ""));
    if (hinted) return hinted;
    // 4) First voice in the requested language as last resort.
    return langMatches[0] || voices[0] || null;
  } catch {
    return null;
  }
}

/**
 * Speak a word/phrase at natural speed using the best available human
 * voice. This is the shared "Hear the word / Read aloud" helper — all
 * TTS across the app should route through this for consistency.
 */
export function speakNatural(text, { rate = 0.9, pitch = 1.0, lang = "en-US" } = {}) {
  return new Promise((resolve) => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      resolve();
      return;
    }
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(String(text || ""));
      u.rate = rate;
      u.pitch = pitch;
      u.lang = lang;
      const v = pickHumanVoice(lang);
      if (v) u.voice = v;
      u.onend = () => resolve();
      u.onerror = () => resolve();
      window.speechSynthesis.speak(u);
    } catch {
      resolve();
    }
  });
}

/**
 * Some browsers (Chrome esp.) load voices asynchronously — the first
 * getVoices() call returns []. Call this once at app boot to warm the
 * cache so pickHumanVoice() gets a real list on the very first speak.
 */
export function warmVoices() {
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  const s = window.speechSynthesis;
  s.getVoices();
  if (typeof s.onvoiceschanged !== "undefined") {
    s.onvoiceschanged = () => { s.getVoices(); };
  }
}
